import type { HealthSource, SourceHealth } from '@ace-tracker/shared';
import { emptyStateKind, SOURCE_LABELS, type EmptyStateKind, type SourceHealthInput } from '../health';

const ICONS: Record<EmptyStateKind, string> = {
  'not-installed': 'fa-solid fa-plug-circle-xmark',
  disabled: 'fa-solid fa-toggle-off',
  stale: 'fa-solid fa-clock-rotate-left',
  zero: 'fa-solid fa-circle-check',
};

const TAGS: Record<EmptyStateKind, { label: string; className: string }> = {
  'not-installed': { label: 'Not installed', className: 'bg-surface-alt text-text-muted' },
  disabled: { label: 'Switched off', className: 'bg-surface-alt text-text-muted' },
  stale: { label: 'Stopped reporting', className: 'bg-warn-bg text-warn-ink' },
  zero: { label: 'No usage', className: 'bg-ok-bg text-ok-ink' },
};

/** Config key that switches each source on, for the "switched off" explanation. */
const CONFIG_KEYS: Record<HealthSource, string> = {
  copilot: 'collection.editors',
  'claude-code': 'collection.agents.claudeCode.enabled',
  codex: 'collection.agents.codex.enabled',
  kiro: 'collection.agents.kiro.enabled',
  gemini: 'collection.agents.gemini.enabled',
  cursor: 'collection.agents.cursor.enabled',
  windsurf: 'collection.agents.windsurf.enabled',
  rtk: 'collection.agents.rtk.enabled',
  headroom: 'collection.agents.headroom.enabled',
  graphify: 'collection.agents.graphify.enabled',
};

function devices(count: number): string {
  return `${count} ${count === 1 ? 'device' : 'devices'}`;
}

function describe(source: HealthSource, health: SourceHealth | undefined, kind: EmptyStateKind, unexplained: boolean): { title: string; body: string } {
  const label = SOURCE_LABELS[source];
  const runs = health?.runs;

  if (unexplained) {
    return {
      title: `No ${label} data has been received`,
      body: `No device has sent ${label} telemetry in the retained period, and no collector has reported why. Collectors older than 1.4.1 cannot say whether a source is installed or switched off; once devices update, this message becomes specific.`,
    };
  }
  switch (kind) {
    case 'not-installed':
      return {
        title: `${label} is not installed on any reporting device`,
        body: `Collectors ran on ${devices(runs?.devices ?? 0)} and found no ${label} data to read. Collection is working; there is nothing to collect.`,
      };
    case 'disabled':
      return {
        title: `${label} collection is switched off`,
        body: `The collector on ${devices(runs?.disabled ?? 0)} has this source switched off in its configuration (${CONFIG_KEYS[source]}). Switch it on to start reporting.`,
      };
    case 'stale': {
      const last = health?.lastObserved ? ` The latest telemetry in this range is from ${new Date(health.lastObserved).toLocaleString()}.` : '';
      const failing = health?.state === 'failing' ? " Every device's latest collector run failed." : '';
      return {
        title: `${label} has stopped reporting`,
        body: `No device has reported in the last 48 hours, out of ${devices(health?.devices.expected ?? 0)} seen recently.${last}${failing} Check that the collector's scheduled task is still running on those devices.`,
      };
    }
    default:
      return {
        title: `No ${label} usage in this range`,
        body: health
          ? `Collectors are reporting (${devices(health.devices.reporting)} in the last 48 hours) and recorded no usage in the selected period. This is a real zero, not missing data.`
          : 'No usage was recorded in the selected period.',
      };
  }
}

/**
 * The one empty state for a source with nothing to show. It says which of
 * four things is true — not installed, switched off, stopped reporting, or
 * genuinely zero — instead of a bare "no data". Driven by collector run
 * reports (see the Data health page); the state is given as a text tag and an
 * icon, never by color alone.
 */
export function SourceEmptyState({ source, health: input }: { source: HealthSource; health?: SourceHealthInput }) {
  if (input === 'loading') {
    return (
      <div role="status" className="rounded-card border border-dashed border-border bg-surface p-5 text-sm">
        <p className="font-medium text-text-default">No {SOURCE_LABELS[source]} data in this range</p>
        <p className="mt-2 text-text-muted">Checking whether it is not installed, switched off, or has stopped reporting…</p>
      </div>
    );
  }
  const health = input;
  const { kind, unexplained } = emptyStateKind(health);
  const { title, body } = describe(source, health, kind, unexplained);
  const tag = unexplained ? { label: 'No report', className: 'bg-surface-alt text-text-muted' } : TAGS[kind];

  return (
    <div role="status" className="rounded-card border border-dashed border-border bg-surface p-5 text-sm">
      <div className="flex flex-wrap items-center gap-2.5">
        <i aria-hidden="true" className={`${ICONS[kind]} text-base text-text-muted`} />
        <p className="font-medium text-text-default">{title}</p>
        <span className={`rounded-pill px-2.5 py-0.5 text-[11px] font-medium ${tag.className}`}>{tag.label}</span>
      </div>
      <p className="mt-2 max-w-3xl text-text-muted">{body}</p>
    </div>
  );
}
